import type { BufferedAttachment } from '@queries/attachments.query';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

const initialState = {
  bufferedByQuestion: {} as Record<string, BufferedAttachment[]>,
};

type SetBufferedAttachmentsParams = {
  attachments: BufferedAttachment[];
  questionAttachmentKey: string;
};

type UpdateBufferedAttachmentsParams = {
  attachment?: BufferedAttachment;
  attachments?: BufferedAttachment[];
  clientId?: string;
  questionAttachmentKey: string;
};

export type SurveyAttachmentsStore = {
  bufferedByQuestion: Record<string, BufferedAttachment[]>;

  clearBufferedAttachments: (questionAttachmentKey: string) => void;
  clearSurveyBufferedAttachments: (surveyId: string) => void;
  getBufferedAttachments: (questionAttachmentKey: string) => BufferedAttachment[];
  hasBufferedAttachments: (questionAttachmentKey: string) => boolean;
  reset: () => void;
  setBufferedAttachments: (params: SetBufferedAttachmentsParams) => void;
  updateBufferedAttachments: (params: UpdateBufferedAttachmentsParams) => void;
};

export const surveyAttachmentsStore = createStore<SurveyAttachmentsStore>((set, get) => ({
  ...initialState,

  clearBufferedAttachments: (questionAttachmentKey) =>
    set((state) => {
      const nextBufferedByQuestion = { ...state.bufferedByQuestion };
      delete nextBufferedByQuestion[questionAttachmentKey];
      return { bufferedByQuestion: nextBufferedByQuestion };
    }),

  clearSurveyBufferedAttachments: (surveyId) =>
    set((state) => {
      const surveyPrefix = `${surveyId}:`;
      const nextBufferedByQuestion = Object.fromEntries(
        Object.entries(state.bufferedByQuestion).filter(([key]) => !key.startsWith(surveyPrefix)),
      );

      return { bufferedByQuestion: nextBufferedByQuestion };
    }),

  getBufferedAttachments: (questionAttachmentKey) => get().bufferedByQuestion[questionAttachmentKey] ?? [],

  hasBufferedAttachments: (questionAttachmentKey) => (get().bufferedByQuestion[questionAttachmentKey] ?? []).length > 0,

  reset: () => set(initialState),

  setBufferedAttachments: (params) => {
    const { attachments, questionAttachmentKey } = params;

    set((state) => ({
      bufferedByQuestion: {
        ...state.bufferedByQuestion,
        [questionAttachmentKey]: attachments,
      },
    }));
  },

  updateBufferedAttachments: (params) => {
    const { attachment, attachments, clientId, questionAttachmentKey } = params;
    const previousAttachments = get().bufferedByQuestion[questionAttachmentKey] ?? [];

    if (attachments) {
      get().setBufferedAttachments({
        attachments,
        questionAttachmentKey,
      });
      return;
    }

    if (attachment && clientId) {
      const nextAttachments = previousAttachments.map((item) => (item.clientId === clientId ? attachment : item));
      get().setBufferedAttachments({
        attachments: nextAttachments,
        questionAttachmentKey,
      });
      return;
    }

    if (attachment) {
      get().setBufferedAttachments({
        attachments: [...previousAttachments, attachment],
        questionAttachmentKey,
      });
    }
  },
}));

export const useSurveyAttachmentsStore = <T>(selector: (state: SurveyAttachmentsStore) => T): T => {
  return useStore(surveyAttachmentsStore, selector);
};
